import React, { createContext, useReducer, useCallback } from 'react';
import { TrackingState, Tracking } from '../types';
import { apiClient } from '../api/client';

// Initial state
const initialState: TrackingState = {
  trackingInfo: {},
  isLoading: false,
  error: null,
};

// Action types
type TrackingAction =
  | { type: 'GET_TRACKING_REQUEST'; payload: string }
  | { type: 'GET_TRACKING_SUCCESS'; payload: Tracking }
  | { type: 'GET_TRACKING_FAILURE'; payload: Error }
  | { type: 'ADD_TRACKING_REQUEST' }
  | { type: 'ADD_TRACKING_SUCCESS'; payload: Tracking }
  | { type: 'ADD_TRACKING_FAILURE'; payload: Error }
  | { type: 'UPDATE_STATUS_REQUEST'; payload: string }
  | { type: 'UPDATE_STATUS_SUCCESS'; payload: Tracking }
  | { type: 'UPDATE_STATUS_FAILURE'; payload: Error }
  | { type: 'VALIDATE_TRACKING_REQUEST' }
  | { type: 'VALIDATE_TRACKING_SUCCESS'; payload: TrackingValidationResponse }
  | { type: 'VALIDATE_TRACKING_FAILURE'; payload: Error }
  | { type: 'CLEAR_ERROR' };

// Tracking validation response interface
interface TrackingValidationResponse {
  valid: boolean;
  used: boolean;
  message: string;
}

// Reducer
const trackingReducer = (state: TrackingState, action: TrackingAction): TrackingState => {
  switch (action.type) {
    case 'GET_TRACKING_REQUEST':
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    case 'ADD_TRACKING_REQUEST':
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    case 'UPDATE_STATUS_REQUEST':
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    case 'VALIDATE_TRACKING_REQUEST':
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    case 'GET_TRACKING_SUCCESS':
    case 'ADD_TRACKING_SUCCESS':
    case 'UPDATE_STATUS_SUCCESS':
      return {
        ...state,
        trackingInfo: {
          ...state.trackingInfo,
          [action.payload.id]: action.payload,
        },
        isLoading: false,
        error: null,
      };
    case 'VALIDATE_TRACKING_SUCCESS':
      return {
        ...state,
        isLoading: false,
        error: null,
      };
    case 'GET_TRACKING_FAILURE':
    case 'ADD_TRACKING_FAILURE':
    case 'UPDATE_STATUS_FAILURE':
    case 'VALIDATE_TRACKING_FAILURE':
      return {
        ...state,
        isLoading: false,
        error: action.payload,
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };
    default:
      return state;
  }
};

// Context
interface TrackingContextType extends TrackingState {
  getTrackingInfo: (id: string) => Promise<Tracking>;
  addTrackingInfo: (transactionId: string, data: Partial<Tracking>) => Promise<Tracking>;
  updateTrackingStatus: (id: string, status: string) => Promise<Tracking>;
  validateTrackingNumber: (trackingNumber: string, carrier: string) => Promise<{valid: boolean, used: boolean, message: string}>;
  clearError: () => void;
}

export const TrackingContext = createContext<TrackingContextType | undefined>(undefined);

// Provider
interface TrackingProviderProps {
  children: React.ReactNode;
}

export const TrackingProvider: React.FC<TrackingProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(trackingReducer, initialState);

  // Get tracking information by ID
  const getTrackingInfo = useCallback(async (id: string): Promise<Tracking> => {
    dispatch({ type: 'GET_TRACKING_REQUEST', payload: id });
    try {
      //Check if we already have the tracking info in state
      if (state.trackingInfo[id]) {
        console.log('[TrackingContext] Returning cached tracking info:', state.trackingInfo[id]);
        // Dispatch success action to reset loading state even when using cached data
        dispatch({ type: 'GET_TRACKING_SUCCESS', payload: state.trackingInfo[id] });
        return state.trackingInfo[id];
      }
      
      // Handle both synchronous and asynchronous implementations
      console.log('[TrackingContext] Calling API to get tracking info for ID:', id);
      const response = apiClient.tracking.getById(id);
      
      // Check if response is a Promise
      const trackingInfo = (response instanceof Promise)
        ? await response as Tracking
        : response as Tracking;
      
      if (!trackingInfo || typeof trackingInfo !== 'object' || !trackingInfo.id) {
        throw new Error('Invalid tracking data received');
      }
      
      // Ensure all required fields are present with default values if missing
      const validatedTracking: Tracking = {
        id: trackingInfo.id,
        transactionId: trackingInfo.transactionId || id,
        trackingNumber: trackingInfo.trackingNumber || 'Unknown',
        carrier: trackingInfo.carrier || 'Unknown',
        status: trackingInfo.status || 'pending',
        deliveryAddress: trackingInfo.deliveryAddress || {
          recipient: 'Unknown',
          street: 'Unknown',
          city: 'Unknown',
          state: 'Unknown',
          postalCode: 'Unknown',
          country: 'Unknown'
        },
        estimatedDeliveryDate: trackingInfo.estimatedDeliveryDate,
        actualDeliveryDate: trackingInfo.actualDeliveryDate,
        statusHistory: trackingInfo.statusHistory || [{
          status: trackingInfo.status || 'pending',
          timestamp: new Date().toISOString(),
          notes: 'Initial status'
        }],
        createdAt: trackingInfo.createdAt || new Date().toISOString(),
        updatedAt: trackingInfo.updatedAt || new Date().toISOString()
      };
      
      dispatch({ type: 'GET_TRACKING_SUCCESS', payload: validatedTracking });
      return validatedTracking;
    } catch (error) {
      console.error('[TrackingContext] Error fetching tracking info:', error);
      dispatch({ type: 'GET_TRACKING_FAILURE', payload: error as Error });
      throw error;
    }
  }, [state.trackingInfo]);

  // Add tracking information to a transaction
  const addTrackingInfo = useCallback(async (transactionId: string, data: Partial<Tracking>): Promise<Tracking> => {
    dispatch({ type: 'ADD_TRACKING_REQUEST' });
    try {
      // Handle both synchronous and asynchronous implementations
      const response = apiClient.tracking.addTracking(transactionId, data);
      
      // Check if response is a Promise
      const trackingInfo = (response instanceof Promise)
        ? await response as Tracking
        : response as Tracking;
      
      if (!trackingInfo || typeof trackingInfo !== 'object' || !trackingInfo.id) {
        throw new Error('Invalid tracking data received');
      }
      
      dispatch({ type: 'ADD_TRACKING_SUCCESS', payload: trackingInfo });
      return trackingInfo;
    } catch (error) {
      dispatch({ type: 'ADD_TRACKING_FAILURE', payload: error as Error });
      throw error;
    }
  }, []);

  // Update tracking status
  const updateTrackingStatus = useCallback(async (id: string, status: string): Promise<Tracking> => {
    dispatch({ type: 'UPDATE_STATUS_REQUEST', payload: id });
    try {
      // Handle both synchronous and asynchronous implementations
      const response = apiClient.tracking.updateStatus(id, status);
      
      // Check if response is a Promise
      const trackingInfo = (response instanceof Promise)
        ? await response as Tracking
        : response as Tracking;
      
      if (!trackingInfo || typeof trackingInfo !== 'object' || !trackingInfo.id) {
        throw new Error('Invalid tracking data received');
      }
      
      dispatch({ type: 'UPDATE_STATUS_SUCCESS', payload: trackingInfo });
      return trackingInfo;
    } catch (error) {
      dispatch({ type: 'UPDATE_STATUS_FAILURE', payload: error as Error });
      throw error;
    }
  }, []);

  // Clear error
  const clearError = useCallback((): void => {
    dispatch({ type: 'CLEAR_ERROR' });
  }, []);

  // Validate tracking number
  const validateTrackingNumber = useCallback(async (trackingNumber: string, carrier: string): Promise<{valid: boolean, used: boolean, message: string}> => {
    dispatch({ type: 'VALIDATE_TRACKING_REQUEST' });
    try {
      // Call the API to validate the tracking number
      const response = await apiClient.tracking.validateTrackingNumber(trackingNumber, carrier);
      
      dispatch({ type: 'VALIDATE_TRACKING_SUCCESS', payload: response });
      return response;
    } catch (error) {
      dispatch({ type: 'VALIDATE_TRACKING_FAILURE', payload: error as Error });
      throw error;
    }
  }, []);

  return (
    <TrackingContext.Provider
      value={{
        ...state,
        getTrackingInfo,
        addTrackingInfo,
        updateTrackingStatus,
        validateTrackingNumber,
        clearError,
      }}
    >
      {children}
    </TrackingContext.Provider>
  );
};